import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Contact from "../components/Contact";
import { SEO, breadcrumbSchema, localBusinessSchema } from "../components/SEO";
import { BRAND } from "../mock";
import { BLOG_POSTS } from "../blog";
import { Clock, Calendar, ArrowUpRight } from "lucide-react";

export default function Blog() {
  const url = `${BRAND.url}/blog`;
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "@id": `${url}#blog`,
    name: "Jester Werks Blog. Ceramic Coating, Paint Correction & Detailing",
    description:
      "Honest guides on ceramic coating, paint correction and auto detailing. written by Mississauga's most-reviewed studio.",
    url,
    publisher: { "@id": `${BRAND.url}/#business` },
    blogPost: BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.description,
      url: `${BRAND.url}/blog/${p.slug}`,
      datePublished: p.date,
      image: p.cover,
      author: { "@type": "Organization", name: BRAND.name },
    })),
  };
  return (
    <div className="grain bg-jester-deep text-white">
      <SEO
        title="Ceramic Coating & Detailing Blog | Jester Werks Mississauga"
        description="Honest, expert guides on ceramic coating, paint correction and auto detailing in Mississauga & the GTA. written by the team behind Mississauga's most-reviewed ceramic coating studio."
        canonical={url}
        schemas={[
          localBusinessSchema(),
          blogSchema,
          breadcrumbSchema([
            { name: "Home", url: `${BRAND.url}/` },
            { name: "Blog", url },
          ]),
        ]}
      />
      <Navbar />

      <section className="relative pt-36 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-acid mb-5 font-label">
           The Jester Werks Blog
          </div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1] max-w-4xl">
            Ceramic coating <span className="text-jester">guides</span>
            <br />from Mississauga's #1 studio.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/70 leading-relaxed">
            Real, transparent guides written by the team behind 1,500+ ceramic
            coating jobs and 250+ verified five-star Google reviews. No fluff.
            No upsells.
          </p>
        </div>
      </section>

      <section className="relative pb-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BLOG_POSTS.map((post, i) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${i}`}
              className="group relative rounded-2xl overflow-hidden border border-white/10 bg-jester-card hover:border-acid/60 transition-all duration-500 flex flex-col"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={post.cover}
                  alt={post.coverAlt || post.title}
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                  width="800"
                  height="500"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#120127] via-transparent to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
                <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center group-hover:bg-acid group-hover:text-[#120127] transition-colors">
                  <ArrowUpRight className="w-4 h-4 text-white group-hover:text-[#120127]" />
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-[10px] text-white/45 uppercase tracking-[0.2em] font-label mb-3">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.date).toLocaleDateString("en-CA", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {post.readMin} min
                  </span>
                </div>
                <h2 className="font-display text-2xl leading-tight group-hover:text-acid transition-colors">
                  {post.title}
                </h2>
                <p className="mt-3 text-sm text-white/65 leading-relaxed flex-1">
                  {post.description}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-acid font-label">
                  Read article <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Contact />
      <Footer />
    </div>
  );
}
